// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/**
 * Producing a unified diff that real `git apply` accepts.
 *
 * "git apply accepts it" is a binary, testable claim, so every rule in this file
 * was established by generating a patch and making git 2.50.1 apply it, not by
 * recalling the format. The measurements are below because they are the reason
 * the code looks the way it does.
 *
 * WHAT GIT REFUSES (each verified, `git apply --check` and real `git apply`
 * agreeing in every case):
 *   • wrong hunk COUNTS               → rc=128, "corrupt patch at line N"
 *   • a patch file with no final \n   → rc=128
 *   • a patch file with CRLF endings  → rc=1
 *   • zero context lines              → rc=1 (even from git's own `diff -U0`)
 *   • bare nested paths, no a/ b/     → rc=1
 * WHAT IT TOLERATES:
 *   • wrong hunk STARTS — they are advisory. We still get them right.
 *   • a missing `index` line, and even an `index` line with wrong blob hashes.
 *     So we emit none: it is dead weight, and its only real effect is to invite
 *     somebody to try `--3way`, which this tool must never encourage.
 *
 * TWO TRAPS THAT COST DATA.
 *
 * 1. `createTwoFilesPatch` CANNOT EXPRESS A DELETION. It writes `+++ b/del.txt`
 *    where `+++ /dev/null` belongs. Measured end-to-end: `git apply` returns
 *    rc=0 — success — and leaves a ZERO-BYTE FILE where the file used to be.
 *    Silent, and the only genuine correctness bug in the library. `formatPatch`
 *    with an explicit `isDelete` is the only correct route.
 *
 * 2. THE SPACE-IN-PATH TAB IS A POST-PROCESS, NOT AN INPUT. git writes a literal
 *    TAB after a path containing a space, and BSD `patch` needs it — without it
 *    `patch -p1` answers "No file to patch". But passing `a/with space.txt\t` as
 *    `oldFileName` makes the library C-quote the whole path, because a tab is a
 *    byte below 0x20 — and git then reports
 *    `error: with space.txt<TAB>: No such file or directory`. The tab has to be
 *    appended after formatting, and never to a path that has already been
 *    C-quoted, since quotes are their own delimiter.
 */

import { formatPatch, structuredPatch } from 'diff';
import type { ChangeKind, Hunk } from '../types';

/**
 * git's default. The library's is 4 — measured, 8 context lines against 6 — and
 * a patch that does not look like `git diff` output is a patch nobody reviews.
 */
export const CONTEXT_LINES = 3;

/**
 * Above this, a diff is refused rather than attempted.
 *
 * The cost of a diff is its edit distance: 5,000 lines at 1% changed is 4 ms,
 * the same file fully rewritten is two seconds. A ceiling is cheaper than an
 * unresponsive tab, and a file this size in a chat reply is not a real edit.
 */
export const MAX_DIFF_BYTES = 8 * 1024 * 1024;

export interface DiffResult {
  readonly hunks: Hunk[];
  /** Set when the diff bailed out and the whole file is one replacement. */
  readonly wholeFile: boolean;
}

/** Both sides must already be LF-normalised — see engine/eol.ts. */
export function diffFile(oldText: string, newText: string): DiffResult {
  if (oldText === newText) return { hunks: [], wholeFile: false };

  const patch = structuredPatch('f', 'f', oldText, newText, undefined, undefined, {
    context: CONTEXT_LINES,
    // Returns `undefined` on bail-out rather than throwing — measured. A
    // `catch` here would never fire and the null check is the real guard.
    maxEditLength: 400_000,
  });

  if (!patch) return { hunks: [wholeFileHunk(oldText, newText)], wholeFile: true };

  return {
    hunks: patch.hunks.map((h) => ({
      oldStart: h.oldStart,
      oldLines: h.oldLines,
      newStart: h.newStart,
      newLines: h.newLines,
      lines: h.lines,
      accepted: true,
    })),
    wholeFile: false,
  };
}

/** The bail-out shape: delete everything, add everything, one hunk. */
function wholeFileHunk(oldText: string, newText: string): Hunk {
  const oldLines = oldText === '' ? [] : oldText.replace(/\n$/, '').split('\n');
  const newLines = newText === '' ? [] : newText.replace(/\n$/, '').split('\n');
  return {
    oldStart: 1,
    oldLines: oldLines.length,
    newStart: 1,
    newLines: newLines.length,
    lines: [...oldLines.map((l) => `-${l}`), ...newLines.map((l) => `+${l}`)],
    accepted: true,
  };
}

/**
 * Re-number the hunks the user kept.
 *
 * When hunk 2 of 3 is rejected, hunk 3 lands at a different line than the full
 * patch said it would. Only `newStart` moves; `oldStart`, `oldLines` and
 * `newLines` of a kept hunk are never touched — a hunk describes itself, and
 * rewriting its counts is exactly the corruption git rejects with rc=128.
 *
 *     newStart_i = oldStart_i + Σ (newLines_j − oldLines_j) for kept j < i
 *
 * git treats starts as hints and would apply the patch either way, verified
 * across five accept/reject subsets on a three-hunk fixture. It is done anyway:
 * the patch is a file a human reads, `git apply --recount` exists because tools
 * that trust starts exist, and it is four lines.
 */
export function renumber(hunks: readonly Hunk[]): Hunk[] {
  let delta = 0;
  const out: Hunk[] = [];
  for (const h of hunks) {
    if (!h.accepted) continue;
    out.push({ ...h, newStart: h.oldStart + delta });
    delta += h.newLines - h.oldLines;
  }
  return out;
}

/**
 * Apply the accepted hunks to the original text, producing the bytes to write.
 *
 * THE TRAILING NEWLINE IS THE WHOLE DIFFICULTY, and the obvious implementation
 * gets four cases wrong — this build's first attempt got exactly those four,
 * caught by the test that cross-checks every result against what real `git
 * apply` produced.
 *
 * A unified diff's lines carry no terminators; `\ No newline at end of file`
 * describes the line ABOVE it, on whichever side that line belongs to. So a
 * marker after a `-` line is a statement about the OLD file and must not touch
 * the new one — which is what makes "a file that GAINS a trailing newline" work.
 * And when the last hunk does not reach the end of the file, the final line is
 * copied from the original and the original's own trailing state is what
 * survives.
 */
export function applyHunks(oldText: string, hunks: readonly Hunk[]): string {
  const accepted = hunks.filter((h) => h.accepted);
  if (accepted.length === 0) return oldText;

  const oldEndsWithNewline = oldText.endsWith('\n');
  const src = oldText === '' ? [] : oldText.replace(/\n$/, '').split('\n');
  const out: string[] = [];
  let cursor = 0; // 0-based index into src
  let newSideHasNoNewline = false;
  let lastLineCameFromHunk = false;

  for (const hunk of accepted) {
    const start = Math.max(0, hunk.oldStart - 1);
    while (cursor < start && cursor < src.length) {
      out.push(src[cursor++]);
      lastLineCameFromHunk = false;
    }
    for (let i = 0; i < hunk.lines.length; i++) {
      const line = hunk.lines[i];
      const marker = line[0];
      const body = line.slice(1);
      if (marker === '-') {
        cursor++;
      } else if (marker === '+') {
        out.push(body);
        lastLineCameFromHunk = true;
      } else if (marker === '\\') {
        const previous = hunk.lines[i - 1] ?? '';
        if (previous.startsWith('+') || previous.startsWith(' ')) newSideHasNoNewline = true;
      } else {
        out.push(body);
        cursor++;
        lastLineCameFromHunk = true;
      }
    }
  }
  while (cursor < src.length) {
    out.push(src[cursor++]);
    lastLineCameFromHunk = false;
  }

  if (out.length === 0) return '';
  const trailing = lastLineCameFromHunk ? !newSideHasNoNewline : oldEndsWithNewline;
  return out.join('\n') + (trailing ? '\n' : '');
}

export interface PatchInput {
  readonly path: string;
  readonly kind: ChangeKind;
  readonly oldText: string;
  readonly newText: string;
  readonly hunks: readonly Hunk[];
}

/** One file's stanza. Returns '' when nothing was accepted. */
export function formatFilePatch(input: PatchInput): string {
  const kept = renumber(input.hunks);
  if (kept.length === 0) return '';

  // `oldHeader`/`newHeader` are typed as required on StructuredPatch but are
  // optional to `formatPatch`, and we deliberately emit neither — a header would
  // become an `Index:` line git ignores and reviewers trip over.
  const formatted = formatPatch({
    oldFileName: input.kind === 'create' ? '/dev/null' : `a/${input.path}`,
    newFileName: input.kind === 'delete' ? '/dev/null' : `b/${input.path}`,
    oldHeader: undefined,
    newHeader: undefined,
    hunks: kept.map((h) => ({
      oldStart: h.oldStart,
      oldLines: h.oldLines,
      newStart: h.newStart,
      newLines: h.newLines,
      lines: [...h.lines],
    })),
    ...(input.kind === 'create' ? { isCreate: true } : {}),
    ...(input.kind === 'delete' ? { isDelete: true } : {}),
  } as Parameters<typeof formatPatch>[0]);

  const lines = formatted.split('\n');
  const start = lines.findIndex((l) => l.startsWith('--- '));
  if (start < 0) return '';
  return lines.slice(start).map(withSpaceTab).join('\n');
}

/** Append git's TAB terminator to a `---`/`+++` line whose path contains a space. */
function withSpaceTab(line: string): string {
  for (const prefix of ['--- ', '+++ ']) {
    if (!line.startsWith(prefix)) continue;
    const rest = line.slice(prefix.length);
    // An already-C-quoted path carries its own delimiters; adding a tab inside
    // or after the quotes breaks it.
    if (rest.startsWith('"') || rest === '/dev/null' || !rest.includes(' ')) return line;
    return `${line}\t`;
  }
  return line;
}

export interface PatchArtefact {
  readonly text: string;
  readonly files: number;
}

/**
 * Assemble the whole patch.
 *
 * `header` is a comment block. git ignores anything before the first `---`, so a
 * patch can carry its own instructions — which matters because the two artefacts
 * this tool emits mean opposite things: `changes.patch` is a patch to RUN, and
 * `applied-<ts>.patch` is a record of what was ALREADY written, and running that
 * one double-applies it.
 */
export function buildPatch(inputs: readonly PatchInput[], header: readonly string[] = []): PatchArtefact {
  const stanzas: string[] = [];
  for (const input of inputs) {
    const stanza = formatFilePatch(input);
    if (stanza) stanzas.push(stanza.endsWith('\n') ? stanza : `${stanza}\n`);
  }
  const prefix = header.length ? header.map((l) => `# ${l}`).join('\n') + '\n' : '';
  return { text: stanzas.length ? prefix + stanzas.join('') : '', files: stanzas.length };
}

export interface Invariant {
  readonly name: string;
  readonly ok: boolean;
}

/**
 * The four properties that decide whether git will read the file at all.
 *
 * Checked on every artefact before it is offered, and asserted in the test
 * suite, because each one was measured to be a hard rc≠0 failure and three of
 * them are invisible on inspection.
 */
export function checkInvariants(patch: string): Invariant[] {
  const body = patch.replace(/^(?:#[^\n]*\n)*/, '');
  const hunkHeaders = body.split('\n').filter((l) => l.startsWith('@@'));
  const stanzas = body.split(/(?=^--- )/m).filter((s) => s.startsWith('--- '));

  let everyHunkHasContext = hunkHeaders.length > 0;
  for (const stanza of stanzas) {
    const chunks = stanza.split(/(?=^@@ )/m).filter((c) => c.startsWith('@@'));
    for (const chunk of chunks) {
      const lines = chunk.split('\n').slice(1);
      // A create or a delete has no context by definition and git accepts it;
      // the rule is about MODIFICATIONS, where zero context is rc=1.
      const isCreate = stanza.startsWith('--- /dev/null');
      const isDelete = /^\+\+\+ \/dev\/null$/m.test(stanza.split('\n')[1] ?? '');
      if (isCreate || isDelete) continue;
      if (!lines.some((l) => l.startsWith(' '))) everyHunkHasContext = false;
    }
  }

  return [
    { name: 'ends with a newline', ok: patch === '' || patch.endsWith('\n') },
    { name: 'contains no carriage return', ok: !patch.includes('\r') },
    { name: 'every modification hunk has context', ok: everyHunkHasContext },
    {
      name: 'every path is a/ or b/ prefixed',
      ok: body
        .split('\n')
        .filter((l) => l.startsWith('--- ') || l.startsWith('+++ '))
        .every((l) => {
          const rest = l.slice(4);
          return rest === '/dev/null' || /^"?[ab]\//.test(rest);
        }),
    },
  ];
}
